import { subject as caslSubjectHelper } from '@casl/ability';
import { ability, Actions, Subjects } from '@boqnet/permissions';
import { usePermissionStore } from '@/stores/permission.store';

/**
 * Composable that resolves a permission check against the CASL ability.
 *
 * Usable in both component `<script setup>` and non-component contexts
 * (stores, utils) since it only depends on Pinia, not Vue lifecycle hooks.
 *
 * @example
 * // In a component or composable
 * const { can } = useCan();
 * const canViewAP = computed(() => can(Actions.LIST, Subjects.ACCOUNTS_PAYABLE));
 *
 * @example
 * // With a per-resource condition (scoped to a specific site/project)
 * const { can } = useCan();
 * can(Actions.READ, Subjects.BOQ, { siteId: '123' });
 */
export const useCan = () => {
  const permissionStore = usePermissionStore();

  const can = (action: Actions, subject: Subjects, data?: Record<string, string>): boolean => {
    // Read abilityVersion to establish a reactive dependency on it.
    // refreshPermissions() increments this counter each time the CASL ability
    // is rebuilt, so any computed that calls can() (e.g. PermissionGate's
    // isAllowed) will automatically re-evaluate after a permissions refresh
    // rather than returning a stale cached result.
    void permissionStore.abilityVersion;
    // The spread is load-bearing. CASL's `subject()` stamps `__caslSubjectType__` onto the
    // object it is handed, so handing it the same object twice under two different subject
    // types throws "Trying to cast object to subject type X but previously it was casted to
    // Y". Callers naturally share one scope object across several checks — a `scope` computed
    // returns one identity for every reader — and that threw during render, blanking the page
    // (PLE-1236). Copying here keeps every call site safe without each one having to know.
    const caslSubject = data ? caslSubjectHelper(subject as string, { ...data }) : subject;
    return ability.can(action, caslSubject as Subjects);
  };

  return { can };
};
